import { forwardRef, useImperativeHandle, useRef } from 'react'
import { Box } from '@mui/material'
import { useAdventureEditorStore } from '../../stores/adventureEditorStore'
import { DescriptionPanel, DescriptionPanelRef } from './panels/DescriptionPanel'
import { SettingsPanel } from './panels/SettingsPanel'
import { GroupsPanel } from './panels/GroupsPanel'
import { TagsPanel } from './panels/TagsPanel'
import { ImagesPanel } from './panels/ImagesPanel'
import { FilesPanel } from './panels/FilesPanel'
import { WorkflowDesignerPanel } from './panels/WorkflowDesignerPanel'
import { MapViewPanel } from './panels/MapViewPanel'
import { CodeManagementPanel } from './panels/CodeManagementPanel'
import { CluesPanel } from './panels/CluesPanel'
import { DisplayCriteriaPanel } from './panels/DisplayCriteriaPanel'
import { JoinCriteriaPanel } from './panels/JoinCriteriaPanel'
import { MaintainerPanel } from './panels/MaintainerPanel'
import { ModeratorPanel } from './panels/ModeratorPanel'
import { TeamPanel } from './panels/TeamPanel'
import type { SectionId } from './AdventureRibbon'

// ====================
// TYPES
// ====================

export interface AdventureContentPanelsRef {
  getDescriptionEditorContent: () => Promise<string>
}

export interface AdventureContentPanelsProps {
  // Editor ribbon props for mobile hamburger menu in workflow designer
  editorMenuProps?: {
    activeSection: SectionId
    onSectionChanged: (section: SectionId) => void
    onEditorSave?: () => void
    onPublish?: () => void
    onDelete?: () => void
    onHistory?: () => void
    onFlowVersionHistory?: () => void
    onImportFlow?: () => void
    isEditorSaving?: boolean
    isPublishing?: boolean
    isReadyToPublish?: boolean
    hiddenSections?: SectionId[]
    workflowAllowed?: boolean
  }
}

// ====================
// COMPONENT
// ====================

export const AdventureContentPanels = forwardRef<AdventureContentPanelsRef, AdventureContentPanelsProps>(({ editorMenuProps }, ref) => {
  const activeSection = useAdventureEditorStore((state) => state.activeSection)
  const adventureId = useAdventureEditorStore((state) => state.adventureId)
  const descriptionPanelRef = useRef<DescriptionPanelRef>(null)

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    getDescriptionEditorContent: async () => {
      if (descriptionPanelRef.current) {
        return await descriptionPanelRef.current.getEditorContentAsync()
      }
      return ''
    },
  }))

  const renderPanel = () => {
    switch (activeSection) {
      case 'description':
        return <DescriptionPanel ref={descriptionPanelRef} />

      case 'settings':
        return <SettingsPanel />

      case 'groups':
        return <GroupsPanel />

      case 'tags':
        return <TagsPanel />

      case 'images':
        return <ImagesPanel />

      case 'files':
        return <FilesPanel />

      case 'locationflow':
        return adventureId ? <WorkflowDesignerPanel adventureId={adventureId} editorMenuProps={editorMenuProps} /> : null

      case 'mapview':
        return <MapViewPanel />

      case 'codes':
        return <CodeManagementPanel />

      case 'clues':
        return <CluesPanel />

      case 'maintainers':
        return <MaintainerPanel />

      case 'moderators':
        return <ModeratorPanel />

      case 'testers':
        return <TeamPanel />

      case 'display':
        return <DisplayCriteriaPanel />

      case 'join':
        return <JoinCriteriaPanel />

      default:
        return <DescriptionPanel ref={descriptionPanelRef} />
    }
  }

  return <>{renderPanel()}</>

})

AdventureContentPanels.displayName = 'AdventureContentPanels'
